import {
  NAV_ITEMS,
  availableReportNavItems,
  filterNavItems,
  type NavItem,
} from '@/navigation/nav-items';
import { REPORTS, type ReportFeature } from '@/reports/catalog';
import type { Permission } from '@shared/permissions';
import type { OmniNavSection } from './types';

function toOmniItem(item: Pick<NavItem, 'id' | 'label' | 'path' | 'icon'>, hint?: string) {
  return {
    key: item.id,
    label: item.label,
    path: item.path,
    icon: item.icon,
    hint,
  };
}

/**
 * Build omni-search page sections from the live sidebar nav + report catalog.
 * Nested nav children (Products, Marketing, …) are listed individually with a parent hint.
 */
export function buildOmniNavSections(options: {
  isTeamActive: boolean;
  hasPermission: (permission: Permission) => boolean;
  /** The team's report features: reports needing one it has off are left out. */
  reportFeatures: ReadonlySet<ReportFeature>;
}): OmniNavSection[] {
  const pages = filterNavItems(NAV_ITEMS, {
    isTeamActive: options.isTeamActive,
    hasPermission: options.hasPermission,
  });

  const pageItems = pages.flatMap((item) => {
    if (!item.children?.length) {
      return [toOmniItem(item)];
    }
    // Children only (avoids duplicate /marketing for parent + Overview).
    // Hint lets omni-search match "marketing" via nav-match.
    return item.children.map((child) =>
      toOmniItem(
        {
          id: child.id,
          label: child.label,
          path: child.path,
          icon: item.icon,
        },
        item.label
      )
    );
  });

  const sections: OmniNavSection[] = [
    {
      key: 'pages',
      label: 'Pages',
      items: pageItems,
    },
  ];

  if (options.isTeamActive) {
    const reportHints = new Map(
      REPORTS.map((r) => {
        const hint = [r.category, r.description].filter(Boolean).join(' · ');
        return [r.path, hint] as const;
      })
    );
    sections.push({
      key: 'reports',
      label: 'Reports',
      items: availableReportNavItems(options.reportFeatures).map((item) =>
        toOmniItem(item, reportHints.get(item.path))
      ),
    });
  }

  return sections;
}
